import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  TimePicker,
} from "antd";
import dayjs from "dayjs";
import { FC, useMemo } from "react";
import { MapPin, User, Clock, Package, Wrench } from "lucide-react";
import { useGetClients } from "~/services/client/client.queries";
import { useGetVesselsForDropdown } from "~/services/vessel/vessel.queries";
import { useGetBusinessAddresses } from "~/services/business-addresses";
import { useBooking } from "~/store";
import { capitalize } from "~/utils";
import {
  ClientStatus,
  JobStatus,
  Maintenance,
  ServiceType,
  Urgency,
  VesselStatus,
} from "~/utils/constants";
import { serviceIcons } from "~/utils/service-icons";
import { AddOnServices } from "./addon-services";
import { AssociatedInventory } from "./associated-inventory";
import { SelectClientAddress } from "./select-client-address";
import { ServiceDatePicker } from "./service-date-picker";
import { StaffAssignment } from "./staff-assignment";
import { Supplies } from "./supplies";
import { PackingEstimator } from "./packing-estimator";
import { NetworkConnect } from "./network-connect";

const mapLabel = {
  [ServiceType.COLLECTION]: "Shipper",
  [ServiceType.DELIVERY]: "Consignee",
  [ServiceType.WAREHOUSE_SERVICE]: "Client",
  [ServiceType.FABRICATION_SERVICE]: "Client",
  [ServiceType.ON_SITE_SERVICE]: "Client",
};

const serviceTypes = [
  { label: "Collection", value: ServiceType.COLLECTION },
  { label: "Delivery", value: ServiceType.DELIVERY },
  { label: "Site Service", value: ServiceType.ON_SITE_SERVICE },
  { label: "Warehouse", value: ServiceType.WAREHOUSE_SERVICE },
  { label: "Fabrication", value: ServiceType.FABRICATION_SERVICE },
];

type Props = {
  handleSubmit: (status: JobStatus, addNew?: boolean) => Promise<void>;
  clearForm: () => void;
};

export const ServiceForm: FC<Props> = ({ handleSubmit, clearForm }) => {
  const form = Form.useFormInstance();
  const { viewMode } = useBooking();

  const serviceType = Form.useWatch("serviceType", form) as ServiceType;
  const serviceDatetime = Form.useWatch("serviceDatetime", form);
  const watchedNetworkRouteId = Form.useWatch("networkRouteId", form);
  const watchedVendorId = Form.useWatch("vendorId", form);
  const watchedShuttleId = Form.useWatch("shuttleId", form);
  const ticketId = form.getFieldValue("id") ?? '';

  const viewOnly = useMemo(() => viewMode === "view", [viewMode]);

  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  const { data: vessels } = useGetVesselsForDropdown();
  const { data: businessAddresses } = useGetBusinessAddresses(true); // Only active addresses

  const handleVesselChange = (selectedVesselId: string) => {
    if (!selectedVesselId || !vessels) return;

    const selectedVessel = vessels.find(
      (vessel) => vessel.id === selectedVesselId
    );

    if (
      selectedVessel &&
      selectedVessel.vesselStaffs &&
      selectedVessel.vesselStaffs.length > 0
    ) {
      const staffIds = selectedVessel.vesselStaffs.map(
        (vesselStaff) => vesselStaff.staff.id
      );

      const prevStaffIds = form.getFieldValue("staffIds") ?? [];

      form.setFieldValue("staffIds", [
        ...new Set([...prevStaffIds, ...staffIds]),
      ]);
    }
  };

  return (
    <div className="p-6 bg-blue-50">
      <Form.Item name="id" noStyle />
      <Form.Item name="networkRouteId" noStyle />
      <Form.Item name="vendorId" noStyle />
      <Form.Item name="shuttleId" noStyle />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Client / Shipper Selection */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <Form.Item
              name="clientId"
              label={
                <span className="flex items-center gap-2">
                  <User size={16} className="text-blue-600" />
                  {mapLabel[serviceType]}
                </span>
              }
              rules={[
                {
                  required: true,
                  message: `Please select a ${mapLabel[
                    serviceType
                  ]?.toLowerCase()}!`,
                },
              ]}
              className="mb-0"
            >
              <Select
                showSearch
                placeholder={`Select ${mapLabel[serviceType]}...`}
                optionFilterProp="children"
                loading={clientsLoading}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={
                  clients?.items?.map((client) => ({
                    value: client.id,
                    label:
                      client.companyName ||
                      [client.firstName, client.lastName]
                        .filter(Boolean)
                        .join(" ")
                        .trim() ||
                      "Unnamed Client",
                  }))
                }
                disabled={viewOnly}
              />
            </Form.Item>
          </div>

          {/* Location Section */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium flex items-center gap-2">
                <MapPin size={16} className="text-blue-600" />
                Location
              </label>
            </div>

            {/* AHC Warehouse Selection - Only for Fabrication & Warehouse Services */}
            {[ServiceType.FABRICATION_SERVICE, ServiceType.WAREHOUSE_SERVICE].includes(
              serviceType
            ) && (
              <Form.Item name="isAhcWarehouse" valuePropName="checked" className="mb-3">
                <Checkbox
                  disabled={viewOnly}
                  onChange={(e) => {
                    if (e.target.checked) {
                      form.setFieldValue("clientAddressId", undefined);
                    } else {
                      form.setFieldValue("businessAddressId", undefined);
                    }
                  }}
                >
                  <span className="font-medium">AHC WHSE</span>
                  <span className="text-gray-500 text-sm ml-2">
                    (Use AHC warehouse address)
                  </span>
                </Checkbox>
              </Form.Item>
            )}

            {/* Show Client Address OR AHC Warehouse Address based on checkbox */}
            <Form.Item noStyle dependencies={["clientId", "isAhcWarehouse"]}>
              {({ getFieldValue }) => {
                const clientId = getFieldValue("clientId");
                const isAhcWarehouse = getFieldValue("isAhcWarehouse");

                if (!clientId) return <div className="text-gray-400 text-sm">Select a client first</div>;

                if (
                  [ServiceType.FABRICATION_SERVICE, ServiceType.WAREHOUSE_SERVICE].includes(
                    serviceType
                  ) &&
                  isAhcWarehouse
                ) {
                  return (
                    <Form.Item
                      name="businessAddressId"
                      label="AHC Warehouse Location"
                      rules={[
                        {
                          required: true,
                          message: "Please select an AHC warehouse location!",
                        },
                      ]}
                      className="mb-0"
                    >
                      <Select
                        placeholder="Select AHC warehouse..."
                        disabled={viewOnly}
                        options={businessAddresses?.map((addr) => ({
                          value: addr.id,
                          label: `${addr.name} - ${addr.address}, ${addr.city}, ${addr.state}`,
                        }))}
                      />
                    </Form.Item>
                  );
                }

                return <SelectClientAddress clientId={clientId} />;
              }}
            </Form.Item>
          </div>

          {/* Scope */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <Form.Item name="scope" label="Scope (visible to client)" className="mb-0">
              <Input.TextArea
                placeholder="Enter job scope..."
                rows={2}
                disabled={viewOnly}
              />
            </Form.Item>
          </div>

          {/* Associated Inventory */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <Form.Item
              name="inventoryIds"
              label="Associated Inventory"
              className="mb-0"
            >
              <AssociatedInventory ticketId={ticketId} showStats />
            </Form.Item>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Network Connect */}
          <NetworkConnect
            serviceType={serviceType}
            networkRouteId={watchedNetworkRouteId}
            vendorId={watchedVendorId}
            shuttleId={watchedShuttleId}
            onNetworkRouteChange={(val) => {
              form.setFieldValue("networkRouteId", val);
              if (val) { form.setFieldValue("vendorId", undefined); form.setFieldValue("shuttleId", undefined); }
            }}
            onVendorChange={(val) => {
              form.setFieldValue("vendorId", val);
              if (val) { form.setFieldValue("networkRouteId", undefined); form.setFieldValue("shuttleId", undefined); }
            }}
            onShuttleChange={(val) => {
              form.setFieldValue("shuttleId", val);
              if (val) { form.setFieldValue("networkRouteId", undefined); form.setFieldValue("vendorId", undefined); }
            }}
          />

          {/* Date & Time */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium">Date & Time</label>
              <span className="text-sm font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded flex items-center gap-1">
                {(() => {
                  const Icon = serviceIcons[serviceType]?.icon;
                  return Icon ? <Icon size={14} /> : null;
                })()}
                {serviceTypes.find(s => s.value === serviceType)?.label}
              </span>
            </div>
            <Form.Item
              name="serviceDatetime"
              label="Service Date"
              rules={[{ required: true, message: "Please select a date!" }]}
              className="mb-3"
            >
              <ServiceDatePicker className="w-full" disabled={viewOnly} serviceType={serviceType} />
            </Form.Item>

            <div className="grid grid-cols-3 gap-3">
              <Form.Item name="startDatetime" label="Start Time" className="mb-0">
                <TimePicker
                  format="h:mm A"
                  use12Hours
                  minuteStep={15}
                  className="w-full"
                  disabled={viewOnly}
                  placeholder="Select time"
                />
              </Form.Item>
              <Form.Item label="Quick" className="mb-0">
                <Select
                  options={[
                    { label: "9AM", value: 9 },
                    { label: "10AM", value: 10 },
                    { label: "11AM", value: 11 },
                    { label: "12PM", value: 12 },
                    { label: "1PM", value: 13 },
                    { label: "2PM", value: 14 },
                    { label: "3PM", value: 15 },
                    { label: "4PM", value: 16 },
                  ]}
                  onChange={(hour: number) => {
                    const serviceDate = form.getFieldValue("serviceDatetime");
                    const baseDate = serviceDate ? dayjs(serviceDate) : dayjs();
                    const combinedDateTime = baseDate.hour(hour).minute(0).second(0).millisecond(0);
                    form.setFieldsValue({ startDatetime: combinedDateTime });
                  }}
                  placeholder="Time"
                  disabled={viewOnly}
                />
              </Form.Item>
              <Form.Item
                name="duration"
                label={
                  <span className="flex items-center gap-1">
                    <Clock size={14} className="text-blue-600" />
                    Duration
                  </span>
                }
                className="mb-0"
              >
                <InputNumber
                  min={0}
                  step={0.25}
                  className="w-full"
                  placeholder="Hours"
                  disabled={viewOnly}
                />
              </Form.Item>
            </div>

            {/* Due Date - Only for Fabrication & Warehouse Services */}
            {[ServiceType.FABRICATION_SERVICE, ServiceType.WAREHOUSE_SERVICE].includes(
              serviceType
            ) && (
              <Form.Item
                name="dueDate"
                label="Due Date"
                rules={[{ required: true, message: "Please select due date!" }]}
                className="mt-3 mb-0"
              >
                <DatePicker
                  placeholder="Select due date..."
                  className="w-full"
                  disabled={viewOnly}
                />
              </Form.Item>
            )}
          </div>

          {/* Urgency & Vessel */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <div className="grid grid-cols-2 gap-3">
              <Form.Item name="urgency" label="Urgency" className="mb-0">
                <Select
                  options={
                    Object.values(Urgency).map((type) => ({
                      label: capitalize(type),
                      value: type,
                    })) ?? []
                  }
                  className="w-full"
                  disabled={viewOnly}
                />
              </Form.Item>
              <Form.Item
                name="vesselId"
                label="Vessel"
                rules={[
                  {
                    required: [
                      ServiceType.COLLECTION,
                      ServiceType.DELIVERY,
                    ].includes(serviceType),
                    message: "Please select a vessel!",
                  },
                ]}
                className="mb-0"
              >
                <Select
                  options={vessels?.map((v) => {
                    const isUnderMaintenance = v.vesselStatus === VesselStatus.MAINTENANCE ||
                      v.maintenance === Maintenance.IN_SERVICE ||
                      v.maintenance === Maintenance.SCHEDULED;
                    const isInactive = v.isActive === false;
                    return {
                      value: v.id,
                      label: v.name,
                      vesselStatus: v.vesselStatus,
                      maintenance: v.maintenance,
                      isActive: v.isActive,
                      disabled: isUnderMaintenance || isInactive,
                    };
                  })}
                  placeholder="Select vessel..."
                  disabled={viewOnly}
                  onChange={handleVesselChange}
                  optionRender={(option) => {
                    const vesselStatus = option.data.vesselStatus as VesselStatus;
                    const maintenance = option.data.maintenance as Maintenance;
                    const isActive = option.data.isActive as boolean;
                    const isUnderMaintenance = vesselStatus === VesselStatus.MAINTENANCE ||
                      maintenance === Maintenance.IN_SERVICE ||
                      maintenance === Maintenance.SCHEDULED;
                    const isInactive = isActive === false;
                    const isInUse = vesselStatus === VesselStatus.IN_USE;
                    const isUnavailable = isUnderMaintenance || isInactive;

                    return (
                      <div className={`flex items-center justify-between ${isUnavailable ? 'opacity-60' : ''}`}>
                        <span className={isUnavailable ? 'line-through text-gray-400' : ''}>
                          {option.label}
                        </span>
                        {isInactive && (
                          <span className="text-xs text-red-500">Inactive</span>
                        )}
                        {isUnderMaintenance && !isInactive && (
                          <span className="flex items-center gap-1 text-xs text-orange-500">
                            <Wrench size={12} />
                            Maintenance
                          </span>
                        )}
                        {isInUse && !isUnavailable && (
                          <span className="text-xs text-blue-500">In Use</span>
                        )}
                      </div>
                    );
                  }}
                />
              </Form.Item>
            </div>
          </div>

          {/* Staff Assignment */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <Form.Item name="staffIds" label="Staff Assignment" className="mb-0">
              <StaffAssignment serviceDate={serviceDatetime ? dayjs(serviceDatetime) : null} />
            </Form.Item>
          </div>

          {/* Staff Notes & Add-On Services */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <Form.Item
                name="staffNotes"
                label="Staff Notes (Internal)"
                className="mb-0"
              >
                <Input.TextArea
                  placeholder="Write something..."
                  rows={3}
                  disabled={viewOnly}
                />
              </Form.Item>
            </div>
            <div className="p-4 bg-white rounded-lg border border-blue-200">
              <AddOnServices />
            </div>
          </div>
        </div>

        {/* Packing Estimator & Supplies - Split 50/50 */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          {/* Packing Estimator */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <label className="text-sm font-medium mb-3 block flex items-center gap-2">
              <Package size={16} className="text-blue-600" />
              Pack Loose Items
            </label>
            <Form.Item noStyle dependencies={["inventoryIds"]}>
              {({ getFieldValue }) => (
                <PackingEstimator
                  ticketId={ticketId}
                  inventoryIds={getFieldValue("inventoryIds")}
                  onSuppliesCalculated={(supplies) => {
                    // Merge calculated supplies with existing supplies
                    const currentSupplies = form.getFieldValue("supplyItems") || [];
                    const merged = [...currentSupplies];
                    for (const calc of supplies) {
                      const existingIdx = merged.findIndex((item: any) => item.supplyId === calc.supplyId);
                      if (existingIdx >= 0) {
                        if (calc.quantity > merged[existingIdx].quantity) {
                          merged[existingIdx] = { ...merged[existingIdx], quantity: calc.quantity, unitCost: calc.unitCost };
                        }
                      } else {
                        merged.push(calc);
                      }
                    }
                    form.setFieldValue("supplyItems", merged);
                  }}
                />
              )}
            </Form.Item>
          </div>

          {/* Supplies */}
          <div className="p-4 bg-white rounded-lg border border-blue-200">
            <Form.Item name="supplyItems" className="mb-0">
              <Supplies />
            </Form.Item>
          </div>
        </div>

        {/* Actions */}
        {!viewOnly ? (
          <div className="col-span-2 flex justify-end items-center gap-3 pt-4 border-t border-blue-200">
            <Popconfirm
              title="Are you sure you want to discard changes?"
              onConfirm={clearForm}
              okText="Yes"
              cancelText="No"
            >
              <Button className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </Button>
            </Popconfirm>
            <Button
              htmlType="button"
              onClick={() => handleSubmit(JobStatus.DRAFT)}
              className="px-4 py-2 bg-white border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Save as Draft
            </Button>
            <Button
              htmlType="button"
              onClick={() => handleSubmit(JobStatus.SCHEDULED)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Service
            </Button>
            <Button
              htmlType="button"
              onClick={() => handleSubmit(JobStatus.SCHEDULED, true)}
              className="px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              Save & Add Another
            </Button>
            <span className="text-sm font-bold text-blue-700 bg-blue-100 px-3 py-1.5 rounded flex items-center gap-1.5">
              {(() => {
                const Icon = serviceIcons[serviceType]?.icon;
                return Icon ? <Icon size={16} /> : null;
              })()}
              {serviceTypes.find(s => s.value === serviceType)?.label}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
